import { Form, FormInstance, Select } from "antd";
import { useEffect, useMemo } from "react";
import { useCidades } from "../../../hooks/useCidades";
import { useEstados } from "../../../hooks/useEstados";
import { usePaises } from "../../../hooks/usePaises";
import { Cidade } from "../../../interfaces/Cidade";
import { Estado } from "../../../interfaces/Estado";
import { Pais } from "../../../interfaces/Pais";
import { cidadeByIdService } from "../../../services/cidadeByIdService";
import { filterSelectOption } from "../utils";

interface Props {
  form: FormInstance;
}

export function LocationFields({ form }: Props) {
  const selectedPaisId = Form.useWatch("paisId", form) as number | undefined;
  const selectedEstadoId = Form.useWatch("estadoId", form) as number | undefined;
  const selectedCidadeId = Form.useWatch("cidadeId", form) as number | undefined;

  const { data: paisesData, isLoading: paisesLoading } = usePaises();
  const { data: estadosData, isLoading: estadosLoading } = useEstados();
  const { data: cidadesData, isLoading: cidadesLoading } = useCidades(
    selectedEstadoId
  );

  const paises = (paisesData ?? []) as Pais[];
  const estados = (estadosData ?? []) as Estado[];
  const cidades = (cidadesData ?? []) as Cidade[];

  const estadosFiltrados = useMemo(() => {
    if (!selectedPaisId) {
      return estados;
    }

    return estados.filter((estado) => estado.paisId === selectedPaisId);
  }, [estados, selectedPaisId]);

  useEffect(() => {
    if (form.getFieldValue("estadoId") || !selectedCidadeId) {
      return;
    }

    let active = true;

    cidadeByIdService(selectedCidadeId).then((cidade: Cidade | undefined) => {
      if (
        active &&
        cidade &&
        form.getFieldValue("cidadeId") === selectedCidadeId &&
        !form.getFieldValue("estadoId")
      ) {
        form.setFieldValue("estadoId", cidade.estadoId);
      }
    });

    return () => {
      active = false;
    };
  }, [form, selectedCidadeId, selectedEstadoId]);

  useEffect(() => {
    if (form.getFieldValue("paisId")) {
      return;
    }

    const estadoId = form.getFieldValue("estadoId");
    if (estadoId) {
      const estado = estados.find((item) => item.id === estadoId);
      if (estado) {
        form.setFieldValue("paisId", estado.paisId);
      }
      return;
    }

    // Aguarda a cidade salva para recuperar o país correto, inclusive fora do Brasil.
    if (form.getFieldValue("cidadeId")) {
      return;
    }

    const brasil = paises.find((pais) =>
      pais.nome.toLowerCase().includes("brasil")
    );
    if (brasil) {
      form.setFieldValue("paisId", brasil.id);
    }
  }, [estados, form, paises, selectedCidadeId, selectedEstadoId, selectedPaisId]);

  return (
    <>
      <Form.Item label="País" name="paisId">
        <Select
          allowClear
          showSearch
          loading={paisesLoading}
          onChange={() => {
            form.setFieldsValue({ estadoId: undefined, cidadeId: undefined });
          }}
          optionFilterProp="label"
          filterOption={filterSelectOption}
          options={paises.map((pais) => ({
            label: pais.nome,
            value: pais.id,
          }))}
          placeholder="Selecione o país"
        />
      </Form.Item>

      <Form.Item
        label="Estado"
        name="estadoId"
        rules={[{ required: true, message: "Selecione o estado." }]}
      >
        <Select
          allowClear
          showSearch
          loading={estadosLoading}
          onChange={() => form.setFieldValue("cidadeId", undefined)}
          optionFilterProp="label"
          filterOption={filterSelectOption}
          options={estadosFiltrados.map((estado) => ({
            label: `${estado.nome} (${estado.abreviacao})`,
            value: estado.id,
          }))}
          placeholder="Selecione o estado"
        />
      </Form.Item>

      <Form.Item
        label="Cidade"
        name="cidadeId"
        rules={[{ required: true, message: "Selecione a cidade." }]}
      >
        <Select
          allowClear
          showSearch
          disabled={!selectedEstadoId}
          loading={cidadesLoading}
          optionFilterProp="label"
          filterOption={filterSelectOption}
          options={cidades.map((cidade) => ({
            label: cidade.nome,
            value: cidade.id,
          }))}
          placeholder="Selecione a cidade"
        />
      </Form.Item>
    </>
  );
}
